import React, { useMemo, useState } from "react";
import Link from "next/link";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

import { ROUTE } from "@/route/app_routes.js";
import { requestPasswordReset } from "@/services/authService";
import { getErrorMessage } from "@/services/apiClient";

const ForgotPasswordForm = () => {
  const { t } = useTranslation();
  const [focusedInput, setFocusedInput] = useState(null);
  const [serverError, setServerError] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  const forgotPasswordSchema = useMemo(
    () =>
      z.object({
        email: z.string().min(1, t("auth.validation.emailRequiredGeneric")).email(t("auth.validation.emailInvalid")),
      }),
    [t]
  );

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(forgotPasswordSchema),
  });

  const onSubmit = async (values) => {
    setServerError(null);
    try {
      await requestPasswordReset(values);
      setSubmitted(true);
    } catch (error) {
      const message = getErrorMessage(error);
      setServerError(message);
      toast.error(message);
    }
  };

  if (submitted) {
    return (
      <div className="rbt-auth-split__form-card">
        <div className="rbt-auth-split__success">
          <span className="rbt-auth-split__success-icon">
            <i className="feather-mail"></i>
          </span>
          <h1 className="title">{t("auth.forgotPassword.checkInbox")}</h1>
          <p>{t("auth.forgotPassword.checkInboxBody")}</p>
          <Link className="rbt-btn btn-border hover-icon-reverse w-100" href={ROUTE.login}>
            <span className="icon-reverse-wrapper">
              <span className="btn-text">{t("auth.forgotPassword.backToSignIn")}</span>
              <span className="btn-icon"><i className="feather-arrow-right"></i></span>
              <span className="btn-icon"><i className="feather-arrow-right"></i></span>
            </span>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="rbt-auth-split__form-card">
      <div className="rbt-auth-split__welcome">
        <h1 className="title">{t("auth.forgotPassword.title")}</h1>
        <p>{t("auth.forgotPassword.subheading")}</p>
      </div>

      {serverError && (
        <div className="rbt-auth-split__alert" role="alert">
          <i className="feather-alert-circle"></i>
          <span>{serverError}</span>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <div className="rbt-auth-split__field">
          <div className={`form-group ${focusedInput === "email" ? "focused" : ""}`}>
            <i className="feather-mail rbt-auth-split__field-icon"></i>
            <input
              id="email"
              type="email"
              placeholder=" "
              autoComplete="email"
              {...register("email")}
              onFocus={() => setFocusedInput("email")}
              onBlur={(e) => { setFocusedInput(null); register("email").onBlur(e); }}
            />
            <label htmlFor="email">{t("auth.emailAddress")}</label>
            <span className="focus-border"></span>
          </div>
          {errors.email && <span className="text-danger d-block mt--5">{errors.email.message}</span>}
        </div>

        <div className="form-submit-group mt--10">
          <button type="submit" className="rbt-btn btn-md btn-gradient hover-icon-reverse w-100" disabled={isSubmitting}>
            <span className="icon-reverse-wrapper">
              <span className="btn-text">
                {isSubmitting && <span className="rbt-auth-split__spinner" aria-hidden="true" />}
                {isSubmitting ? t("auth.forgotPassword.sending") : t("auth.forgotPassword.sendResetLink")}
              </span>
              <span className="btn-icon"><i className="feather-arrow-right"></i></span>
              <span className="btn-icon"><i className="feather-arrow-right"></i></span>
            </span>
          </button>
        </div>

        <p className="rbt-auth-split__legal">
          {t("auth.rememberedPassword")} <Link href={ROUTE.login}>{t("auth.signIn")}</Link>
        </p>
      </form>
    </div>
  );
};

export default ForgotPasswordForm;
